export const renderShadowHand = (ctx: CanvasRenderingContext2D, width: number, height: number, progress: number = 1.0, grabProgress: number = 0.0) => {
    ctx.save();

    // Scale and center the hand
    const scale = Math.min(width / 300, height / 300);
    ctx.scale(scale, scale);
    ctx.translate(150, 150);

    ctx.globalAlpha = Math.min(1, 1.2 * progress);

    const baseShadow = '#020b14'; // Almost black blue
    const armorPanel = '#06173b'; // Dark blue leather/metal
    const neonCyan = '#00ffff';
    const gold = '#ffd700';

    // Helper for finger segments to look like armored plates
    const drawArmoredFinger = (x: number, y: number, length: number, angle: number, isThumb: boolean, rings: number) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);

        // When grabbing, we simulate perspective curling by shrinking the finger length and drawing a crease
        const curlScale = 1 - (grabProgress * (isThumb ? 0.3 : 0.45));
        const curLen = length * curlScale;
        const width = isThumb ? 24 : 18;

        // Base shadow shape
        ctx.beginPath();
        ctx.moveTo(-width / 2, 0);
        ctx.lineTo(-width * 0.4, -curLen * 0.8);
        ctx.lineTo(0, -curLen - width); // sharp claw tip
        ctx.lineTo(width * 0.4, -curLen * 0.8);
        ctx.lineTo(width / 2, 0);
        ctx.closePath();
        ctx.fillStyle = baseShadow;
        ctx.fill();

        // Armored Panel
        ctx.beginPath();
        ctx.moveTo(-width * 0.4, 0);
        ctx.lineTo(-width * 0.3, -curLen * 0.8);
        ctx.lineTo(0, -curLen - width * 0.8);
        ctx.lineTo(width * 0.3, -curLen * 0.8);
        ctx.lineTo(width * 0.4, 0);
        ctx.closePath();
        ctx.fillStyle = armorPanel;
        ctx.fill();

        // Neon Cyan Edges (Left and Right slits)
        ctx.beginPath();
        ctx.moveTo(-width * 0.4, 5);
        ctx.lineTo(-width * 0.35, -curLen * 0.7);
        ctx.strokeStyle = neonCyan;
        ctx.lineWidth = 2;
        ctx.shadowColor = neonCyan;
        ctx.shadowBlur = 8;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(width * 0.4, 5);
        ctx.lineTo(width * 0.35, -curLen * 0.7);
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Gold Rings
        if (rings > 0) {
            for (let i = 0; i < rings; i++) {
                const ry = -curLen * (0.3 + i * 0.25);
                ctx.beginPath();
                ctx.ellipse(0, ry, width * 0.45, 4, 0, 0, Math.PI * 2);
                ctx.fillStyle = gold;
                ctx.shadowColor = '#ffaa00';
                ctx.shadowBlur = 10;
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.ellipse(-width * 0.2, ry, width * 0.1, 1.5, Math.PI / 6, 0, Math.PI * 2);
                ctx.shadowBlur = 0;
                ctx.fill();
            }
        }

        // Knuckle Crease (shows when grabbing)
        if (grabProgress > 0.1) {
            const creaseY = -curLen * 0.5;
            ctx.beginPath();
            ctx.moveTo(-width * 0.35, creaseY);
            ctx.quadraticCurveTo(0, creaseY + 5 * grabProgress, width * 0.35, creaseY);
            ctx.strokeStyle = `rgba(0, 255, 255, ${grabProgress})`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }

        ctx.restore();
    }

    // Cuff / Arm
    ctx.beginPath();
    ctx.moveTo(-45, 120);
    ctx.lineTo(-35, 60);
    ctx.lineTo(35, 60);
    ctx.lineTo(45, 120);
    ctx.closePath();
    ctx.fillStyle = baseShadow;
    ctx.fill();

    // Fingers drawn BEFORE palm so they sit "under" the top knuckles 
    // Pinky
    drawArmoredFinger(-32, -15, 65, -0.4 + (grabProgress * 0.1), false, 1);
    // Ring
    drawArmoredFinger(-12, -25, 85, -0.15 + (grabProgress * 0.05), false, 2);
    // Middle
    drawArmoredFinger(10, -30, 95, 0.05, false, 0);
    // Index
    drawArmoredFinger(30, -20, 80, 0.3 - (grabProgress * 0.1), false, 1);

    // Thumb drawn below palm but on right side, it curves slightly over
    drawArmoredFinger(45, 20, 70, 0.8 - (grabProgress * 0.5), true, 1);

    // Palm / Back of Hand Plate
    ctx.beginPath();
    ctx.moveTo(-35, 60); // bottom left
    ctx.lineTo(-44, 0); // mid left
    ctx.quadraticCurveTo(-45, -30, -25, -35); // top left knuckle
    ctx.lineTo(25, -40); // top right knuckle
    ctx.quadraticCurveTo(55, -20, 50, 20); // right side thumb base
    ctx.lineTo(35, 60); // bottom right
    ctx.closePath();

    const palmGrad = ctx.createLinearGradient(0, -40, 0, 60);
    palmGrad.addColorStop(0, armorPanel);
    palmGrad.addColorStop(1, baseShadow);
    ctx.fillStyle = palmGrad;
    ctx.fill();

    // Neon trim for palm plate
    ctx.beginPath();
    ctx.moveTo(-35, 60);
    ctx.lineTo(-44, 0);
    ctx.quadraticCurveTo(-45, -30, -25, -35);
    ctx.lineTo(25, -40);
    ctx.quadraticCurveTo(55, -20, 50, 20);
    ctx.lineTo(35, 60);
    ctx.strokeStyle = neonCyan;
    ctx.lineWidth = 3;
    ctx.shadowColor = neonCyan;
    ctx.shadowBlur = 15;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Center Arcane Star Emblem
    ctx.save();
    ctx.translate(5, 5);
    ctx.scale(1.2, 1.2);
    ctx.strokeStyle = neonCyan;
    ctx.lineWidth = 1.5;
    ctx.shadowColor = neonCyan;
    ctx.shadowBlur = 10;

    // Outer circle
    ctx.beginPath();
    ctx.arc(0, 0, 15, 0, Math.PI * 2);
    ctx.stroke();

    // Hexagram
    ctx.beginPath();
    ctx.moveTo(0, -10); ctx.lineTo(9, 5); ctx.lineTo(-9, 5); ctx.closePath();
    ctx.moveTo(0, 10); ctx.lineTo(9, -5); ctx.lineTo(-9, -5); ctx.closePath();
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(0, 0, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();

    // Knuckle glowing slits
    const drawKnuckleSlit = (x: number, y: number, angle: number) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.moveTo(-6, 0); ctx.lineTo(6, 0);
        ctx.strokeStyle = neonCyan;
        ctx.lineWidth = 2;
        ctx.shadowColor = neonCyan;
        ctx.shadowBlur = 5;
        ctx.stroke();
        ctx.restore();
    };

    drawKnuckleSlit(-32, -28, -0.4);
    drawKnuckleSlit(-12, -38, -0.15);
    drawKnuckleSlit(10, -43, 0.05);
    drawKnuckleSlit(30, -33, 0.3);

    ctx.restore();
};
